import type { VinculoEstipulanteItem } from '../types/estipulanteVinculoType';
import type { GenerateAttendanceProtocolReturn } from '../types/vida/protocol/protocolType';

/** Vida ramo in the Estipulante vinculo (`parsed.ramoCodigo` in the legacy key) — aligned to ramo 4 in the policy in `listar-apol-perf-usuario`. */
export const VIDA_VINCULO_RAMO_CODE = 4;

/** Namespaced keys — same pattern as `company.selectedVinculoId` in `companyStore`. */
export const VIDA_ID_PROTOCOLO_STORAGE_KEY = 'company.vida.idProtocolo';
export const VIDA_CHAVE_PROTOCOLO_STORAGE_KEY = 'company.vida.chaveProtocolo';

/** ACCEPT number or string (JSON/BFF sometimes returns string); avoid clearing LS by `Number.isFinite` false. */
function coerceIdProtocolo(value: number | string | null | undefined): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const digits = String(value ?? '').replace(/\D/g, '');
  const n = Number(digits);
  return Number.isFinite(n) ? n : Number.NaN;
}

/** Normalize the `retorno` from the BFF for persistence and store (numeric id, trimmed chave). */
export function normalizeGerarProtocoloRetorno(
  retorno: GenerateAttendanceProtocolReturn | null | undefined,
): GenerateAttendanceProtocolReturn | null {
  if (!retorno) {
    return null;
  }
  const idNum = coerceIdProtocolo(retorno.idProtocolo as unknown as number | string | null | undefined);
  const chave = String(retorno.chaveProtocolo ?? '').trim();
  if (!Number.isFinite(idNum) || idNum <= 0 || !chave) {
    return null;
  }
  return {
    idProtocolo: idNum,
    chaveProtocolo: chave,
    execucao: String(retorno.execucao ?? 'S'),
  };
}

/**
 * Grava sempre as duas chaves no `localStorage`. Valores inválidos ou nulos viram string vazia
 * (evita credenciais antigas após erro de API ou retorno malformado; reload lê só credenciais válidas).
 */
export function persistVidaProtocolCredentials(
  idProtocolo: number | string | null | undefined,
  chaveProtocolo: string | null | undefined,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const idNum = coerceIdProtocolo(idProtocolo);
  const chave = String(chaveProtocolo ?? '').trim();
  if (!Number.isFinite(idNum) || idNum <= 0 || !chave) {
    window.localStorage.setItem(VIDA_ID_PROTOCOLO_STORAGE_KEY, '');
    window.localStorage.setItem(VIDA_CHAVE_PROTOCOLO_STORAGE_KEY, '');
    return;
  }
  window.localStorage.setItem(VIDA_ID_PROTOCOLO_STORAGE_KEY, String(idNum));
  window.localStorage.setItem(VIDA_CHAVE_PROTOCOLO_STORAGE_KEY, chave);
}

export function clearPersistedVidaProtocol(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(VIDA_ID_PROTOCOLO_STORAGE_KEY);
  window.localStorage.removeItem(VIDA_CHAVE_PROTOCOLO_STORAGE_KEY);
}

export function getPersistedVidaProtocol(): { idProtocolo: number; chaveProtocolo: string } | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const idRaw = window.localStorage.getItem(VIDA_ID_PROTOCOLO_STORAGE_KEY);
  const chave = window.localStorage.getItem(VIDA_CHAVE_PROTOCOLO_STORAGE_KEY);
  if (idRaw == null || chave == null) {
    return null;
  }
  const idTrim = idRaw.trim();
  const chaveTrim = chave.trim();
  if (!idTrim || !chaveTrim) {
    return null;
  }
  const idProtocolo = Number(idTrim);
  if (!Number.isFinite(idProtocolo) || idProtocolo <= 0) {
    return null;
  }
  return { idProtocolo, chaveProtocolo: chaveTrim };
}

export function isVinculoEstipulanteRamoVida(item: VinculoEstipulanteItem | null | undefined): boolean {
  if (!item) {
    return false;
  }
  const n = Number(String(item.parsed.ramoCodigo ?? '').replace(/\D/g, ''));
  return Number.isFinite(n) && n === VIDA_VINCULO_RAMO_CODE;
}

/**
 * If the vinculo is ramo Vida (4), read `idProtocolo` / `chaveProtocolo` from `localStorage`,
 * rewrite with {@link persistVidaProtocolCredentials} (normalization) and return the `retorno`
 * to hydrate the Zustand store of attendance protocol. Otherwise return `null`.
 */
export function readRefreshPersistedProtocolRetornoForRamo4Vinculo(
  item: VinculoEstipulanteItem | null | undefined,
): GenerateAttendanceProtocolReturn | null {
  if (!isVinculoEstipulanteRamoVida(item)) {
    return null;
  }
  const creds = getPersistedVidaProtocol();
  if (!creds) {
    return null;
  }
  // Regrava via util após leitura (normalização / mesma fonte de verdade do persist).
  persistVidaProtocolCredentials(creds.idProtocolo, creds.chaveProtocolo);
  return {
    idProtocolo: creds.idProtocolo,
    chaveProtocolo: creds.chaveProtocolo,
    execucao: 'S',
  };
}
